/**
 * MTJ ERP — File-based i18n (lazy per-language)
 *
 * Architecture:
 *   - English is bundled eagerly because it is also the fallback for every other language.
 *   - Other languages (hi / mr / bn) are dynamically imported on demand via Vite's code-splitter,
 *     keeping initial JS bundle small and startup fast.
 *   - Coverage / availability metadata lives in LANGUAGE_INFO and gates the language picker UI.
 *   - English fallback is applied for any missing key (see LanguageContext.tsx).
 *
 * Coverage re-counted directly from source files on 2026-07-18 (the
 * previous 2025-06-30 figures below LANGUAGE_INFO had drifted well out of
 * date — hi/mr/bn have all gained translations since, but LANGUAGE_INFO's
 * coveragePct numbers had NOT been updated to match, overstating actual
 * coverage to the user-facing language picker):
 *   - en: 100% — baseline
 *   - hi:  74% localized; missing keys fall back to English
 *   - mr:  36% localized; missing keys fall back to English
 *   - bn:  36% localized; missing keys fall back to English
 *
 * Per the production mandate ("hide incomplete languages until fully translated"), only
 * languages flagged `enabled: true` here are exposed in the user-facing switcher. A
 * Super-Owner override (Settings → Language → Enable beta languages) bypasses the gate
 * for translators / internal QA only. mr/bn are currently enabled at 31% coverage —
 * whether that meets the "fully translated" bar is a product decision, not something
 * this audit changes; the fallback-to-English logic in LanguageContext.tsx means no
 * blank/broken string can ever appear regardless of this setting.
 */

// ---- English (eager) ----
import enAuth from "./en/auth";
import enBilling from "./en/billing";
import enBranches from "./en/branches";
import enCatalog from "./en/catalog";
import enCommon from "./en/common";
import enCrm from "./en/crm";
import enDashboard from "./en/dashboard";
import enExpenses from "./en/expenses";
import enLedger from "./en/ledger";
import enManufacturing from "./en/manufacturing";
import enMelt from "./en/melt";
import enNavigation from "./en/navigation";
import enNotifications from "./en/notifications";
import enOrders from "./en/orders";
import enPeople from "./en/people";
import enRepair from "./en/repair";
import enReports from "./en/reports";
import enSettings from "./en/settings";
import enStock from "./en/stock";
import enWorkers from "./en/workers";
import enTour from "./en/tour";
import enWorkshop from "./en/workshop";

export type LanguageCode = "en" | "mr" | "hi" | "bn";
export type TranslationDictionary = Record<string, Record<string, string>>;

const enDict: TranslationDictionary = {
  auth: enAuth,
  billing: enBilling,
  branches: enBranches,
  catalog: enCatalog,
  common: enCommon,
  crm: enCrm,
  dashboard: enDashboard,
  expenses: enExpenses,
  ledger: enLedger,
  manufacturing: enManufacturing,
  melt: enMelt,
  navigation: enNavigation,
  notifications: enNotifications,
  orders: enOrders,
  people: enPeople,
  repair: enRepair,
  reports: enReports,
  settings: enSettings,
  stock: enStock,
  tour: enTour,
  workers: enWorkers,
  workshop: enWorkshop,
};

// ---- Lazy loaders ----
// Vite splits each call into its own chunk; the chunk is fetched only when the user picks
// the language. Missing or placeholder modules fall back to the bundled English dictionary.
const safeImport = async <T extends Record<string, string>>(
  loader: () => Promise<{ default: T }>,
  fallback: T,
): Promise<T> => {
  try {
    const mod = await loader();
    return mod.default ?? fallback;
  } catch {
    return fallback;
  }
};

async function loadHi(): Promise<TranslationDictionary> {
  const [
    auth,
    billing,
    branches,
    catalog,
    common,
    crm,
    dashboard,
    expenses,
    ledger,
    manufacturing,
    melt,
    navigation,
    notifications,
    orders,
    people,
    repair,
    reports,
    settings,
    stock,
    tour,
    workers,
    workshop,
  ] = await Promise.all([
    safeImport(() => import("./hi/auth"), enAuth),
    safeImport(() => import("./hi/billing"), enBilling),
    safeImport(() => import("./hi/branches"), enBranches),
    safeImport(() => import("./hi/catalog"), enCatalog),
    safeImport(() => import("./hi/common"), enCommon),
    safeImport(() => import("./hi/crm"), enCrm),
    safeImport(() => import("./hi/dashboard"), enDashboard),
    safeImport(() => import("./hi/expenses"), enExpenses),
    safeImport(() => import("./hi/ledger"), enLedger),
    safeImport(() => import("./hi/manufacturing"), enManufacturing),
    safeImport(() => import("./hi/melt"), enMelt),
    safeImport(() => import("./hi/navigation"), enNavigation),
    safeImport(() => import("./hi/notifications"), enNotifications),
    safeImport(() => import("./hi/orders"), enOrders),
    safeImport(() => import("./hi/people"), enPeople),
    safeImport(() => import("./hi/repair"), enRepair),
    safeImport(() => import("./hi/reports"), enReports),
    safeImport(() => import("./hi/settings"), enSettings),
    safeImport(() => import("./hi/stock"), enStock),
    safeImport(() => import("./hi/tour"), enTour),
    safeImport(() => import("./hi/workers"), enWorkers),
    safeImport(() => import("./hi/workshop"), enWorkshop),
  ]);
  return {
    auth,
    billing,
    branches,
    catalog,
    common,
    crm,
    dashboard,
    expenses,
    ledger,
    manufacturing,
    melt,
    navigation,
    notifications,
    orders,
    people,
    repair,
    reports,
    settings,
    stock,
    tour,
    workers,
    workshop,
  };
}

async function loadMr(): Promise<TranslationDictionary> {
  const [
    auth,
    billing,
    branches,
    catalog,
    common,
    crm,
    dashboard,
    expenses,
    ledger,
    manufacturing,
    melt,
    navigation,
    notifications,
    orders,
    people,
    repair,
    reports,
    settings,
    stock,
    tour,
    workers,
    workshop,
  ] = await Promise.all([
    safeImport(() => import("./mr/auth"), enAuth),
    safeImport(() => import("./mr/billing"), enBilling),
    safeImport(() => import("./mr/branches"), enBranches),
    safeImport(() => import("./mr/catalog"), enCatalog),
    safeImport(() => import("./mr/common"), enCommon),
    safeImport(() => import("./mr/crm"), enCrm),
    safeImport(() => import("./mr/dashboard"), enDashboard),
    safeImport(() => import("./mr/expenses"), enExpenses),
    safeImport(() => import("./mr/ledger"), enLedger),
    safeImport(() => import("./mr/manufacturing"), enManufacturing),
    safeImport(() => import("./mr/melt"), enMelt),
    safeImport(() => import("./mr/navigation"), enNavigation),
    safeImport(() => import("./mr/notifications"), enNotifications),
    safeImport(() => import("./mr/orders"), enOrders),
    safeImport(() => import("./mr/people"), enPeople),
    safeImport(() => import("./mr/repair"), enRepair),
    safeImport(() => import("./mr/reports"), enReports),
    safeImport(() => import("./mr/settings"), enSettings),
    safeImport(() => import("./mr/stock"), enStock),
    safeImport(() => import("./mr/tour"), enTour),
    safeImport(() => import("./mr/workers"), enWorkers),
    safeImport(() => import("./mr/workshop"), enWorkshop),
  ]);
  return {
    auth,
    billing,
    branches,
    catalog,
    common,
    crm,
    dashboard,
    expenses,
    ledger,
    manufacturing,
    melt,
    navigation,
    notifications,
    orders,
    people,
    repair,
    reports,
    settings,
    stock,
    tour,
    workers,
    workshop,
  };
}

async function loadBn(): Promise<TranslationDictionary> {
  const [
    auth,
    billing,
    branches,
    catalog,
    common,
    crm,
    dashboard,
    expenses,
    ledger,
    manufacturing,
    melt,
    navigation,
    notifications,
    orders,
    people,
    repair,
    reports,
    settings,
    stock,
    tour,
    workers,
    workshop,
  ] = await Promise.all([
    safeImport(() => import("./bn/auth"), enAuth),
    safeImport(() => import("./bn/billing"), enBilling),
    safeImport(() => import("./bn/branches"), enBranches),
    safeImport(() => import("./bn/catalog"), enCatalog),
    safeImport(() => import("./bn/common"), enCommon),
    safeImport(() => import("./bn/crm"), enCrm),
    safeImport(() => import("./bn/dashboard"), enDashboard),
    safeImport(() => import("./bn/expenses"), enExpenses),
    safeImport(() => import("./bn/ledger"), enLedger),
    safeImport(() => import("./bn/manufacturing"), enManufacturing),
    safeImport(() => import("./bn/melt"), enMelt),
    safeImport(() => import("./bn/navigation"), enNavigation),
    safeImport(() => import("./bn/notifications"), enNotifications),
    safeImport(() => import("./bn/orders"), enOrders),
    safeImport(() => import("./bn/people"), enPeople),
    safeImport(() => import("./bn/repair"), enRepair),
    safeImport(() => import("./bn/reports"), enReports),
    safeImport(() => import("./bn/settings"), enSettings),
    safeImport(() => import("./bn/stock"), enStock),
    safeImport(() => import("./bn/tour"), enTour),
    safeImport(() => import("./bn/workers"), enWorkers),
    safeImport(() => import("./bn/workshop"), enWorkshop),
  ]);
  return {
    auth,
    billing,
    branches,
    catalog,
    common,
    crm,
    dashboard,
    expenses,
    ledger,
    manufacturing,
    melt,
    navigation,
    notifications,
    orders,
    people,
    repair,
    reports,
    settings,
    stock,
    tour,
    workers,
    workshop,
  };
}

const LOADERS: Record<Exclude<LanguageCode, "en">, () => Promise<TranslationDictionary>> = {
  hi: loadHi,
  mr: loadMr,
  bn: loadBn,
};

/** In-memory cache so a language is fetched only once per session. */
const DICT_CACHE: Partial<Record<LanguageCode, TranslationDictionary>> = { en: enDict };

/** Returns the cached dictionary for `lang`, loading it from a code-split chunk if needed. */
export async function loadLanguageDictionary(lang: LanguageCode): Promise<TranslationDictionary> {
  const cached = DICT_CACHE[lang];
  if (cached) return cached;
  const loader = LOADERS[lang as Exclude<LanguageCode, "en">];
  if (!loader) return enDict;
  const dict = await loader();
  DICT_CACHE[lang] = dict;
  return dict;
}

/** Synchronous accessor — use only when you know the dictionary has been preloaded. */
export function getLoadedDictionary(lang: LanguageCode): TranslationDictionary {
  return DICT_CACHE[lang] ?? enDict;
}

/** Always-available English baseline (used as the cross-language fallback). */
export const englishDictionary: TranslationDictionary = enDict;

// ---- UI metadata: gates the language picker ----
export interface LanguageInfo {
  code: LanguageCode;
  /** Short label shown in the compact selector (e.g. EN, हिं). */
  label: string;
  /** Native name shown in dropdowns. */
  native: string;
  /** Pre-computed coverage vs English baseline (0-100). */
  coveragePct: number;
  /** Whether the language is production-ready (shown in the user-facing picker by default). */
  enabled: boolean;
}

export const LANGUAGE_INFO: Record<LanguageCode, LanguageInfo> = {
  en: { code: "en", label: "EN", native: "English", coveragePct: 100, enabled: true },
  hi: { code: "hi", label: "हिं", native: "हिन्दी", coveragePct: 74, enabled: true },
  mr: { code: "mr", label: "मरा", native: "मराठी", coveragePct: 36, enabled: true },
  bn: { code: "bn", label: "বাং", native: "বাংলা", coveragePct: 36, enabled: true },
};

export const ALL_LANGUAGES: LanguageCode[] = ["en", "hi", "mr", "bn"];
export function getEnabledLanguages(includeBeta = false): LanguageCode[] {
  return ALL_LANGUAGES.filter((l) => LANGUAGE_INFO[l].enabled || includeBeta);
}

// Backwards-compat: legacy components imported `translations` as a sync record.
// We surface it as a getter that always returns at least English plus any languages
// that have already been pre-loaded into the cache. New code should call
// `loadLanguageDictionary(lang)` and consume the result asynchronously.
export const translations = new Proxy(
  {},
  {
    get(_, prop: string) {
      if (prop === "en") return enDict;
      return DICT_CACHE[prop as LanguageCode];
    },
    ownKeys() {
      return Object.keys(DICT_CACHE);
    },
    getOwnPropertyDescriptor() {
      return { enumerable: true, configurable: true };
    },
  },
) as Record<LanguageCode, TranslationDictionary>;
